import axios, { type AxiosResponse, HttpStatusCode } from "axios";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import api from "@/routes/api";
import {
  type FetchSubscriptionResponseData,
  getInitialSubscriptionRules,
  type SubscriptionStore,
  type SubscriptionStoreState,
} from "./subscription-store.types";

const initialSubscriptionState: SubscriptionStoreState = {
  teamId: null,
  team: null,
  intervals: [],
  productPrices: [],
  levels: [],
  subscription: {
    id: null,
    stripe_id: null,
    level: null,
    is_active: false,
    is_grace_period: false,
    activated_at: null,
    current_period_ends_at: null,
    subscription_ends_at: null,
    subscription_product_price: null,
    custom_price_per_quantity: {
      price_as_string: null,
      price_with_currency: null,
    },
    usage: {
      can_add_user_to_team: null,
      can_add_client_session: null,
      can_add_custom_template: null,
    },
    licences: {
      available: 1,
      used: 1,
    },
    trial: {
      is_active: true,
      ends_at: null,
      days_remaining: null,
    },
    plan_limits: getInitialSubscriptionRules(),
  },
  initialStateIsLoading: false,
  upgradeIsLoading: false,
};

export const useSubscriptionStore = create<SubscriptionStore>()(
  persist(
    (set, get) => ({
      ...initialSubscriptionState,

      fetchSubscription: async (teamId: number) => {
        set({ teamId });

        const response: AxiosResponse<FetchSubscriptionResponseData> =
          await axios.get(api.workspaces.subscription.url(teamId));

        if (response.status !== HttpStatusCode.Ok) {
          const subscriptionStoreState = get();
          subscriptionStoreState.setIsFetchingInitialSubscriptionState(false);
          subscriptionStoreState.setUpgradeIsLoading(false);

          if (response.status === HttpStatusCode.Forbidden) {
            // Most likely called too early when the user hasn't authenticated or verified their email
            return;
          }

          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const {
          subscription,
          team,
          intervals,
          product_prices: productPrices,
          levels,
        } = response.data;

        set({ subscription, intervals, productPrices, levels, team });

        const state = get();

        state.setIsFetchingInitialSubscriptionState(false);
        state.setUpgradeIsLoading(false);
      },

      resetState: () => set(initialSubscriptionState),
      setIsFetchingInitialSubscriptionState: (isLoading: boolean) =>
        set({ initialStateIsLoading: isLoading }),
      setUpgradeIsLoading: (isLoading: boolean) =>
        set({ upgradeIsLoading: isLoading }),
    }),
    {
      name: "app-subscription-store",
      storage: createJSONStorage(() => sessionStorage),
    }
  )
);
